import { Component, OnInit } from "@angular/core";
import { AccountDTO } from "../Account";
import { ActivatedRoute, Router } from "@angular/router";
import { isUndefined, omit, remove, find, orderBy } from "lodash";
import { AccountService } from "../account.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { UiService } from "src/app/ui/ui.service";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";

@Component({
  selector: "app-catalog",
  templateUrl: "./catalog.component.html",
  styleUrls: ["./catalog.component.scss"],
})
export class CatalogComponent implements OnInit {
  account?: AccountDTO;
  saveDisabled: boolean = false;
  lookupHeaders: any[] = [];
  /**
   * Snapshot of profile field IDs present when the component initialized.
   * Used to distinguish persisted fields from newly added ones so that
   * only the original fields participate in sorting.
   */
  private initialProfileFieldIds = new Set<string>();

  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private AccountService: AccountService,
    private NotificationService: NotificationService,
    private uiService: UiService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.account = this.activatedRoute.snapshot.data.account;

    if (!this.account) {
      return;
    }

    // Ensure catalog root exists before setting nested defaults.
    if (!this.account.catalog || typeof this.account.catalog !== "object") {
      (this.account as any).catalog = {};
    }

    // account_app_devices timeout
    if (isUndefined(this.account?.catalog.account_app_devices)) {
      this.account!.catalog.account_app_devices = {
        timeout: 86400,
      };
    }

    if (isUndefined(this.account?.catalog.account_app_devices.timeout)) {
      this.account!.catalog.account_app_devices.timeout = 86400;
    }

    // signal template
    if (isUndefined(this.account?.catalog.signalTemplates)) {
      this.account!.catalog.signalTemplates = {
        styles: null,
      };
    }

    if (isUndefined(this.account?.catalog.signalTemplates.styles)) {
      this.account!.catalog.signalTemplates.styles = null;
    }

    // signals
    if (isUndefined(this.account?.catalog.signals)) {
      this.account!.catalog.signals = {
        payload: {
          display_form: false,
          lookups: [],
        },
      };
    }

    // signals.payload
    if (isUndefined(this.account?.catalog.signals.payload)) {
      this.account!.catalog.signals.payload = {
        display_form: false,
        lookups: [],
      };
    }

    // signals.payload.display_form
    if (isUndefined(this.account?.catalog.signals.payload.display_form)) {
      this.account!.catalog.signals.payload.display_form = false;
    }

    // signals.payload.display_form.lookups
    if (isUndefined(this.account?.catalog.signals.payload.lookups)) {
      this.account!.catalog.signals.payload.lookups = [];
    }

    // Lookup headers
    this.account!.catalog.signals.payload.lookups.forEach(
      (lookup: any, idx: number) => {
        this.lookupHeaders.push(
          Object.keys(lookup.headers).map((k) => {
            return {
              key: k,
              value: lookup.headers[k],
            };
          }) || [],
        );
      },
    );

    // app_users
    if (isUndefined(this.account?.catalog.app_users)) {
      this.account!.catalog.app_users = {
        profile: [],
      };
    }

    // app_users.profile
    if (isUndefined(this.account?.catalog.app_users.profile)) {
      this.account!.catalog.app_users.profile = [];
    }

    // Capture initial profile field IDs for persisted fields
    this.account!.catalog.app_users.profile.forEach((field: any) => {
      if (field.id != null) {
        this.initialProfileFieldIds.add(String(field.id));
      }
    });
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  sortedFields(fields: any): any[] {
    if (!Array.isArray(fields)) {
      return [];
    }

    // Fields that existed at component init time (by ID) are considered
    // "persisted" and should be sorted.
    const persistedFields = fields.filter((field: any) =>
      field.id != null && this.initialProfileFieldIds.has(String(field.id)),
    );

    // Any fields not in the initial ID set are treated as "new" and
    // appended after the sorted list, unsorted.
    const newFields = fields.filter(
      (field: any) =>
        field.id == null || !this.initialProfileFieldIds.has(String(field.id)),
    );

    return [
      ...orderBy(persistedFields, ["order", "id"], ["asc", "asc"]),
      ...newFields,
    ];
  }

  addLookup() {
    this.account!.catalog.signals.payload.lookups.push({
      name: "",
      method: "",
      url: "",
      headers: {},
      label_path: "",
      value_path: "",
    });
    this.lookupHeaders.push([]);
  }

  removeLookup(index: number) {
    this.account!.catalog.signals.payload.lookups.splice(index, 1);
    this.lookupHeaders.splice(index, 1);
  }

  addHeader(lookupIndex: number): void {
    var key = prompt("Enter header key name");
    if (
      key != null &&
      this.account!.catalog.signals.payload.lookups[lookupIndex].headers[
        key
      ] === undefined
    ) {
      this.account!.catalog.signals.payload.lookups[lookupIndex].headers[key] =
        "value";
      this.lookupHeaders[lookupIndex].push({
        key: key,
        value: "value",
      });
    }
  }

  headerValueChange(
    lookupIndex: number,
    headerKey: number,
    newValue: any,
  ): void {
    Object.keys(
      this.account!.catalog.signals.payload.lookups[lookupIndex].headers,
    )
      .sort()
      .forEach((h: any, hIdx: number) => {
        if (h == headerKey) {
          this.account!.catalog.signals.payload.lookups[lookupIndex].headers[
            h
          ] = newValue;
        }
      });
  }

  headerDelete(lookupIndex: number, key: any) {
    this.account!.catalog.signals.payload.lookups[lookupIndex].headers = omit(
      this.account!.catalog.signals.payload.lookups[lookupIndex].headers,
      key,
    );
    remove(this.lookupHeaders[lookupIndex], (h: any) => {
      return h.key === key;
    });
  }

  profileFieldAdd() {
    const fields = this.account!.catalog.app_users.profile;
    // Determine the next order value based on existing fields.
    const maxOrder = fields.length
      ? Math.max(
          ...fields.map((field: any) =>
            typeof field.order === "number" ? field.order : 0,
          ),
        )
      : 0;

    fields.push({
      id: "field" + (this.account!.catalog.app_users.profile.length + 1),
      name: "",
      _type: "String",
      field: "",
      // Place new fields at the end of the ordered list.
      order: maxOrder + 1,
    });
  }

  saveCatalog(): void {
    this.uiService
      .waitForEvent(
        this.AccountService.updateAccountCatalog(
          this.account!.id,
          this.account as AccountDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar(
            "Account catalog updated successfully",
          );
        },
        () => {
          this.NotificationService.openErrorSnackBar(
            "Error updating account catalog",
          );
        },
      );
  }
}
